import React, { createContext, useContext, useState, useEffect } from "react"
import { api, setUnauthorizedHandler } from "../api/api"
import { fetchCardSettings } from "../api/Cards"
import { useEventNotifications } from '../Notifications/NotificationScript.js'
import { fetchSingleAccount } from "../api/Cards/users"

export const UserContext = createContext(null)
export const useUser = () => useContext(UserContext)

export const UserProvider = ({ children }) => {
    const getRole = (roleId) => {
        switch (roleId) {
            case 0:
                return "User"
            case 1:
                return "Reseller"
            case 2:
                return "Admin"
            case 3:
                return "SuperAdmin"
            default:
                return null
        }
    }

    const [remoteProps, setRemoteProps] = useState(null)

    const [user, setUser] = useState(() => {
        const storedData = JSON.parse(localStorage.getItem("userData"))

        if (storedData) {
            try {
                const { token, id, name, role, roleId } = storedData
                const userData = {
                    token,
                    id,
                    name,
                    role,
                    roleId
                }

                return userData
            } catch (error) {
                console.error("Invalid token:", error)
                localStorage.removeItem("userToken")
                return null
            }
        }
        return null
    })

    const { setup } = useEventNotifications()

    const getToken = () => JSON.parse(localStorage.getItem("userData")).token

    const login = async (formData) => {
        try {
            const response = await api.post("/User/login", {
                eMail: formData.get("Username"),
                password: formData.get("Password")
            })
            if (!response.ok) {
                return { success: false, message: "Användarnamnet eller lösenordet är felaktigt." }
            }
            const responseData = await response.text()
            const userData = JSON.parse(responseData).UserData

            const mainToken = responseData.substring(responseData.indexOf("MainToken") + 11, responseData.indexOf(',"SubToken"'))
            const subToken = responseData.substring(responseData.indexOf("SubToken") + 10, responseData.indexOf('},"'))
            const token = {
                mainToken,
                subToken
            }
            const role = getRole(userData.Role)
            const id = userData.UserID
            const username = `${userData.FirstName.trim()} ${userData.LastName.trim()}`

            localStorage.setItem(
                "userData",
                JSON.stringify({
                    token,
                    id,
                    name: username,
                    role,
                    roleId: userData.Role,
                    address: userData.Address,
                    firstname: userData.FirstName,
                    lastname: userData.LastName,
                    resellername: userData.ResellerName,
                })
            )

            setUser({ username, token, role, id, roleId: userData.Role })

            fetchSingleAccount({ Token: token, UserID: id }).then(function (data) {

                if (typeof data === "number") {
                    console.log("Error: ", data)
                    return
                }

                if (data.bPushNotify) {
                    setup(token);
                }
            })

            return { success: true }
        } catch (error) {
            console.error("Login error:", error)
            return { success: false, message: error.toString() }
        }
    }

    const logout = async () => {

        const response = await api.post("/User/logout", getToken())

        setUser(null)
        localStorage.removeItem("userData")
    }

    const changePassword = async (username, oldPassword, newPassword) => {
        if (!username) {
            return { success: false, message: "Användarnamn krävs." }
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Authentication/update_user`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: user ? `Bearer ${user.token}` : ""
                },
                body: JSON.stringify({ username, oldPassword, newPassword })
            })

            if (!response.ok) {
                return { success: false, message: "Failed to change password." }
            }

            return { success: true }
        } catch (error) {
            console.error("Change password error:", error)
            return { success: false, message: error.toString() }
        }
    }

    const resetPassword = async (username) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/User/reset_password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ EMail: username })
            })

            if (!response.ok) {
                return { success: false, message: "Failed to send reset password email." }
            }

            return { success: true }
        } catch (error) {
            console.error("Reset password error:", error)
            return { success: false, message: error.toString() }
        }
    }

    const resetPasswordConfirm = async (id, newPassword) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/User/reset_password_confirm/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ Key:id, Password:newPassword })
            })

            if (!response.ok) {
                return { success: false, message: "Failed to reset password." }
            }

            return { success: true }
        } catch (error) {
            console.error("Reset password confirm error:", error)
            return { success: false, message: error.toString() }
        }
    }

    const isAdmin = user && (user.role === "Admin" || user.role === "SuperAdmin")
    const isReadOnly = user && user.role === "Reseller"

    useEffect(() => {
        setUnauthorizedHandler(logout)
    }, [])

    const value = { isReadOnly, user, isAdmin, getRole, getToken, login, logout, changePassword, resetPassword, resetPasswordConfirm, remoteProps, setRemoteProps }

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export default UserProvider
